import React, { useState } from 'react';
import { Coffee, Activity } from 'lucide-react';
import { formatHm } from './tokens';

/**
 * Phase 4 — Fatigue alert modal.
 * Soft, non-alarming. Triggers when online_seconds crosses preferences.fatigue_threshold_hours.
 *
 * Props:
 *   onlineSeconds — current session online time (or daily online if you prefer)
 *   thresholdHours — driver's configured threshold
 *   onTakeBreak({ minutes }) — pause requests for N minutes
 *   onContinue() — dismiss without pausing
 *
 * The component keeps its own "snooze" state so it can offer 15/30/60-min options.
 */
const FatigueAlertModal = ({ onlineSeconds = 0, thresholdHours = 4, onTakeBreak, onContinue }) => {
  const [picked, setPicked] = useState(15);
  const overSeconds = Math.max(0, onlineSeconds - thresholdHours * 3600);

  return (
    <div className="fixed inset-0 z-[78] bg-black/50 backdrop-blur-[2px] flex items-end sm:items-center justify-center p-3" data-testid="fatigue-alert">
      <div
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 12px)' }}
      >
        <div className="bg-gradient-to-br from-amber-50 to-rose-50 p-5 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-white shadow-md flex items-center justify-center mb-2">
            <Coffee className="w-7 h-7 text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Time for a quick break?</h3>
          <p className="text-[13px] text-gray-600 mt-1">
            You've been online for <span className="font-semibold tabular-nums">{formatHm(onlineSeconds)}</span>
            {overSeconds > 0 ? <> · {formatHm(overSeconds)} past your goal.</> : '.'}
          </p>
          <p className="text-[12px] text-gray-500 mt-1.5">
            Short breaks keep ratings high and earnings safer. We'll pause new requests for the time you pick.
          </p>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="fatigue-snooze-options">
            {[15, 30, 60].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setPicked(m)}
                data-testid={`fatigue-snooze-${m}`}
                className={`h-12 rounded-2xl border-2 text-[13px] font-semibold transition-colors ${
                  picked === m
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 text-gray-600 hover:border-emerald-200'
                }`}
              >
                {m} min
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => onTakeBreak?.({ minutes: picked })}
            data-testid="fatigue-take-break"
            className="w-full h-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/25"
          >
            <Coffee className="w-4 h-4" /> Take {picked} min break
          </button>
          <button
            type="button"
            onClick={onContinue}
            data-testid="fatigue-continue"
            className="w-full h-12 mt-2 rounded-full bg-white text-gray-700 font-semibold border border-gray-200 inline-flex items-center justify-center gap-2 hover:bg-gray-50"
          >
            <Activity className="w-4 h-4" /> I'm fine, continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default FatigueAlertModal;
